import { useEffect, useState } from 'react';
import { useData } from '../contexts/DataContext';
import { useLiveSupport } from './useLiveSupport';

export interface LiveSupportRow {
  id: string;
  status: string;
  summary?: string;
  createdBy?: { uid?: string; role?: string };
}

/**
 * Subscribe to live support queue via the data adapter.
 * Mock adapter returns an empty queue (UI stays ticket-only in demo).
 */
export function useSupportLiveQueue(
  houseId: string,
  houseIds?: string[],
  enabled = true,
) {
  const { adapter } = useData();
  const { heartbeatPresence, claimSession, closeLiveSession } = useLiveSupport();
  const [rows, setRows] = useState<LiveSupportRow[]>([]);

  useEffect(() => {
    if (!enabled || !houseId) return undefined;
    const ids = houseIds?.length ? houseIds : [houseId];
    const beat = () => { void heartbeatPresence(ids); };
    beat();
    const timer = window.setInterval(beat, 20_000);
    return () => window.clearInterval(timer);
  }, [enabled, houseId, houseIds, heartbeatPresence]);

  useEffect(() => {
    if (!enabled || !houseId) {
      setRows([]);
      return undefined;
    }
    if (!adapter.subscribeSupportQueue) {
      setRows([]);
      return undefined;
    }
    return adapter.subscribeSupportQueue(houseId, (next) => {
      setRows(next as unknown as LiveSupportRow[]);
    });
  }, [enabled, houseId, adapter]);

  return { rows, claimSession, closeLiveSession };
}
